import React, { useRef, useEffect } from "react";

const CANVAS_SIZE = 600; // px - increased for better visibility
const GRID_STEP_IN = 1; // Draw grid every 2 inches
const MAX_VISUAL_INCHES = 6; // Maximum range to display (6 inches)

/**
 * SwayVisualizer draws the sway path matching the reference chart style.
 * Data is received in feet, displayed in inches.
 * - X axis: Left/Right movement (sway_sideways_ft)
 * - Y axis: Front/Back movement (sway_forward_backward_ft)
 */

interface Props {
  swayX?: number[]; // in feet (left/right)
  swayY?: number[]; // in feet (forward/backward)
  showCurrentDot?: boolean; // debug: show black dot at latest position
}
export default function SwayVisualizer({ swayX = [], swayY = [], showCurrentDot = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // console.log("🔄 SwayVisualizer useEffect triggered", {
    //   swayXLength: swayX.length,
    //   swayYLength: swayY.length,
    //   swayXSample: swayX.slice(0, 3),
    //   swayYSample: swayY.slice(0, 3),
    //   canDrawPath: swayX.length >= 2 && swayY.length >= 2
    // });

    const canvas = canvasRef.current;
    if (!canvas) {
      // console.warn("⚠️ Canvas ref is null!");
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      // console.warn("⚠️ Could not get 2d context!");
      return;
    }

    // Clear canvas fully before redrawing
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (swayX.length === 0 || swayY.length === 0) {
      // console.log("⏸️ No data yet, waiting...");
      return;
    }

    // Need at least 2 points to draw a path
    if (swayX.length < 2 || swayY.length < 2) {
      // console.log(`⏸️ Only ${swayX.length} point(s) - need at least 2 to draw path. Waiting for more data...`);
      // Still draw the grid and reference point even with just 1 point
      // So the chart is visible and ready
    }

    // console.log("✅ Data available, starting visualization...", {
    //   pointCount: Math.min(swayX.length, swayY.length),
    //   willDrawPath: swayX.length >= 2 && swayY.length >= 2
    // });

    const centerX = CANVAS_SIZE / 2;
    const centerY = CANVAS_SIZE / 2;

    // Scale: pixels per inch (leave 20% margin for labels)
    const margin = 0.8;
    const usableRadius = (CANVAS_SIZE / 2) * margin;

    // Convert data from feet to inches
    const swayXNormalized = swayX.map((x) => {
      if (!Number.isFinite(x)) return 0;
      return x * 12; // Always convert feet to inches
    });
    const swayYNormalized = swayY.map((y) => {
      if (!Number.isFinite(y)) return 0;
      return y * 12; // Always convert feet to inches
    });

    // Find first valid point for reference (should be at or near 0, 0)
    let firstValidIndex = -1;
    let firstXValue = 0;
    let firstYValue = 0;

    for (let i = 0; i < swayXNormalized.length; i++) {
      if (
        Number.isFinite(swayXNormalized[i]) &&
        Number.isFinite(swayYNormalized[i])
      ) {
        if (firstValidIndex === -1) {
          firstValidIndex = i;
          firstXValue = swayXNormalized[i];
          firstYValue = swayYNormalized[i];
          // If first point is exactly (0, 0), that's the reference frame - perfect!
          // Data is already relative to reference, so we don't need to subtract
        }
      }
    }

    // Fixed 6-inch display range — no auto-scaling.
    // Points outside the outermost circle are simply not drawn (pen lifts off).
    const displayRangeInches = MAX_VISUAL_INCHES;

    // Visual scale: pixels per inch
    const scale = usableRadius / displayRangeInches;
    const maxRadiusInches = Math.ceil(displayRangeInches);

    // console.log("Grid and scale info:", {
    //   usableRadius,
    //   displayRangeInches,
    //   scale,
    //   maxRadiusInches,
    //   center: { centerX, centerY },
    // });

    // Draw concentric circles (grid) every 2 inches
    ctx.save();
    ctx.strokeStyle = "#0F854C"; // Green for grid
    ctx.lineWidth = 6;
    for (let r = GRID_STEP_IN; r <= maxRadiusInches; r += GRID_STEP_IN) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, r * scale, 0, 2 * Math.PI);
      ctx.stroke();
    }
    ctx.restore();

    // Draw cross axes (horizontal and vertical lines)
    ctx.save();
    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, CANVAS_SIZE);
    ctx.moveTo(0, centerY);
    ctx.lineTo(CANVAS_SIZE, centerY);
    ctx.stroke();
    ctx.restore();

    // Draw green reference dot at center (drawn later, after path)
    // We'll draw it at the end so it's visible on top

    // Draw distance markers and labels on the right side (aligned with horizontal midline)
    ctx.save();
    ctx.fillStyle = "#000";
    ctx.font = "12px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    for (let r = GRID_STEP_IN; r <= maxRadiusInches; r += GRID_STEP_IN) {
      const markerX = centerX + r * scale;
      const labelX = markerX + 8; // 8px offset for label
      ctx.fillText(`${r}"`, labelX, centerY);
    }
    ctx.restore();

    // Draw directional labels (outside the outermost ring)
    const outerRadius = maxRadiusInches * scale;
    ctx.save();
    ctx.fillStyle = "#000";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "center";
    // Front (top) - positioned outside the chart
    ctx.textBaseline = "bottom";
    ctx.fillText("Front", centerX, Math.max(10, centerY - outerRadius - 15));
    // Back (bottom) - positioned outside the chart
    ctx.textBaseline = "top";
    ctx.fillText(
      "Back",
      centerX,
      Math.min(CANVAS_SIZE - 10, centerY + outerRadius + 15)
    );
    // Left - positioned outside the chart
    ctx.textBaseline = "middle";
    ctx.textAlign = "right";
    ctx.fillText("Left", Math.max(10, centerX - outerRadius - 15), centerY);
    // Right - positioned outside the chart
    ctx.textAlign = "left";
    ctx.fillText(
      "Right",
      Math.min(CANVAS_SIZE - 10, centerX + outerRadius + 15),
      centerY
    );
    ctx.restore();

    // Draw the path - normalize so first point is always at center
    ctx.save();
    ctx.strokeStyle = "#000000"; // Black path
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    // Build segments of points that are inside the outermost circle.
    // When a point goes outside the circle, the "pen lifts" — no line is drawn.
    // When the point re-enters, a new segment starts from the re-entry point.
    const outerRadiusPx = maxRadiusInches * scale; // outermost circle radius in pixels
    const segments: Array<Array<{ px: number; py: number }>> = [];
    let currentSegment: Array<{ px: number; py: number }> = [];

    for (let i = 0; i < swayXNormalized.length; i++) {
      const xInches = swayXNormalized[i];
      const yInches = swayYNormalized[i];

      if (!Number.isFinite(xInches) || !Number.isFinite(yInches)) {
        continue;
      }

      const xRelative = xInches - firstXValue;
      const yRelative = yInches - firstYValue;
      const px = centerX - xRelative * scale;
      const py = centerY + yRelative * scale;

      if (!Number.isFinite(px) || !Number.isFinite(py)) {
        continue;
      }

      // Check if point is inside the outermost circle
      const dx = px - centerX;
      const dy = py - centerY;
      const distFromCenter = Math.sqrt(dx * dx + dy * dy);

      if (distFromCenter <= outerRadiusPx) {
        // Inside circle — pen down, add to current segment
        currentSegment.push({ px, py });
      } else {
        // Outside circle — pen up, end current segment if it has points
        if (currentSegment.length > 0) {
          segments.push(currentSegment);
          currentSegment = [];
        }
      }
    }
    // Push the last segment if it has points
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    // Draw each segment independently (pen-up between segments)
    for (const seg of segments) {
      if (seg.length === 1) {
        // Single point — draw a small dot so it's visible
        ctx.beginPath();
        ctx.arc(seg[0].px, seg[0].py, 2, 0, 2 * Math.PI);
        ctx.fillStyle = "#000000";
        ctx.fill();
      } else if (seg.length === 2) {
        ctx.beginPath();
        ctx.moveTo(seg[0].px, seg[0].py);
        ctx.lineTo(seg[1].px, seg[1].py);
        ctx.stroke();
      } else if (seg.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(seg[0].px, seg[0].py);

        const firstMidX = (seg[0].px + seg[1].px) / 2;
        const firstMidY = (seg[0].py + seg[1].py) / 2;
        ctx.lineTo(firstMidX, firstMidY);

        for (let i = 1; i < seg.length - 1; i++) {
          const midX = (seg[i].px + seg[i + 1].px) / 2;
          const midY = (seg[i].py + seg[i + 1].py) / 2;
          ctx.quadraticCurveTo(seg[i].px, seg[i].py, midX, midY);
        }

        const last = seg[seg.length - 1];
        ctx.lineTo(last.px, last.py);
        ctx.stroke();
      }
    }
    ctx.restore();

    // Draw green reference dot at center (reference point)
    ctx.save();
    ctx.fillStyle = "#00ff00"; // Bright green
    ctx.beginPath();
    ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();

    // Draw current position indicator (black dot) at latest point - debug only
    if (showCurrentDot &&
      swayXNormalized.length > 0 &&
      swayYNormalized.length > 0 &&
      firstValidIndex >= 0
    ) {
      const lastX = swayXNormalized[swayXNormalized.length - 1];
      const lastY = swayYNormalized[swayYNormalized.length - 1];

      if (Number.isFinite(lastX) && Number.isFinite(lastY)) {
        const xRelative = lastX - firstXValue;
        const yRelative = lastY - firstYValue;
        const currentPx = centerX - xRelative * scale;
        const currentPy = centerY + yRelative * scale;

        if (Number.isFinite(currentPx) && Number.isFinite(currentPy)) {
          const dxDot = currentPx - centerX;
          const dyDot = currentPy - centerY;
          const dotDist = Math.sqrt(dxDot * dxDot + dyDot * dyDot);
          // Only draw if inside the circle and not at dead center
          if (dotDist <= outerRadiusPx && dotDist > 1) {
            ctx.save();
            ctx.fillStyle = "#000000";
            ctx.beginPath();
            ctx.arc(currentPx, currentPy, 4, 0, 2 * Math.PI);
            ctx.fill();
            ctx.restore();
          }
        }
      }
    }
  }, [swayX, swayY]);

  return (
    <div
      style={{
        position: "absolute",
        borderRadius: "8px",
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 8px",
        width: "100vw",
        height: "calc(100vh - 75px)",
        top: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "white",
      }}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        style={{ display: "block" }}
      />
    </div>
  );
}
